import React, { Component } from 'react'
import { Col, Row } from 'react-bootstrap';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter, Paper } from "@mui/material";
import { formatAmount } from './../../helper';
import SectionHeader from '../../Pages/admin/shared/SectionHeader';
import { NotApprovedRange } from '../../Pages/admin/shared/NotApprovedTag';

/* Shop Summary — the grid.

   Legacy shape, from SP_MS_FMS_RptShopSummery_Query: one row per shop, the selected weeks on
   the left and the SAME figures for every week BEFORE the range on the right (the proc's PW*
   columns), which is the brought-forward position.

   Seventeen columns is genuinely what this report is, so the table scrolls inside its own
   container rather than squeezing the figures.

   Two things legacy did not show, added because the numbers are otherwise unexplainable:
     - the shop's calc type, since that is what decides whether Total is cash, card or both;
     - a "settles at" note on any shop whose takings transfer elsewhere, since its Total is 0
       while its Cash and Card are not. */

const headCell = { background: '#f1f1f1', color: '#111827', fontWeight: 600, whiteSpace: 'nowrap' };
const groupCell = { background: '#ffefd6', color: '#111827', fontWeight: 700, textAlign: 'center', whiteSpace: 'nowrap' };
const numCell = { textAlign: 'right', whiteSpace: 'nowrap' };
const footCell = { fontWeight: 800, textAlign: 'right', color: '#111827', background: '#ffefd6', whiteSpace: 'nowrap' };
const prevTint = { background: '#fbfbfb' };

/*
 * The 17-column grid as data, grouped exactly as the header already grouped it. Each group
 * renders as its own table (see the comment at the render site), and `rowKey` doubles as the
 * key into both a row and the totals object — they share field names, which is what lets the
 * footer be driven from the same definition.
 */
const COLUMN_GROUPS = [
    {
        title: 'Selected Weeks',
        columns: [
            { label: 'Cash', rowKey: 'TotalCash' },
            { label: 'Card', rowKey: 'TotalCard' },
            { label: 'Total', rowKey: 'TotalAmount', bold: true },
            { label: 'Other Shop Cash', rowKey: 'OSTotalCash' },
            { label: 'Other Shop Card', rowKey: 'OSTotalCard' },
            { label: 'Pay By Shop', rowKey: 'TotalPayByShop' },
            { label: 'Pay By Account', rowKey: 'TotalPayByAcc' },
            { label: 'Delivery', rowKey: 'TotalDelivery' }
        ]
    },
    {
        title: 'Brought Forward (all earlier weeks)',
        tint: prevTint,
        columns: [
            { label: 'Cash', rowKey: 'PWTotalCash' },
            { label: 'Card', rowKey: 'PWTotalCard' },
            { label: 'Total', rowKey: 'PWTotalAmount', bold: true },
            { label: 'Other Shop Cash', rowKey: 'PWOSTotalCash' },
            { label: 'Other Shop Card', rowKey: 'PWOSTotalCard' },
            { label: 'Pay By Shop', rowKey: 'PTotalPayment' },
            { label: 'Pay By Account', rowKey: 'PTotalPayByAcc' }
        ]
    }
];

class Report extends Component {

    render() {
        const { ReportDetails = {} } = this.props;
        const rows = ReportDetails.rows || [];
        const totals = ReportDetails.totals || {};

        return (
            <section>
                <SectionHeader title={<>Shop Summary &nbsp;|&nbsp; {this.props.WeekRange}<NotApprovedRange weeks={this.props.UnapprovedWeeks} /></>} />

                <Row>
                    <Col md={12} >
                        {/*
                          * Split into two stacked tables, one per header group.
                          *
                          * The full grid is 17 columns (Shop, Type + 8 "Selected Weeks" + 7
                          * "Brought Forward"), over the 12-column cap. Rather than cutting at an
                          * arbitrary 12th column — which would slice a header group in half — it
                          * breaks on the group boundary that already exists: 10 columns, then 9.
                          * Shop and Type repeat at the start of each block so every row stays
                          * identifiable. See REPORT_MAX_COLUMNS in helper.js.
                          */}
                        {COLUMN_GROUPS.map((group) => (
                            <Paper key={group.title} style={{ marginBottom: '18px' }} >
                                <TableContainer className="report-table-container" >
                                    <Table className="report-table pc-swipe-table" size="small" stickyHeader aria-label={`shop summary — ${group.title}`}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell style={groupCell} colSpan={2} ></TableCell>
                                                <TableCell style={groupCell} colSpan={group.columns.length} >{group.title}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell style={headCell} >Shop</TableCell>
                                                <TableCell style={headCell} >Type</TableCell>
                                                {group.columns.map((c) => (
                                                    <TableCell key={c.label} style={{ ...headCell, ...numCell, ...(group.tint || {}) }} >{c.label}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {rows.length === 0 &&
                                                <TableRow>
                                                    <TableCell colSpan={group.columns.length + 2} style={{ textAlign: 'center' }} >
                                                        There were no results for matching criteria
                                                    </TableCell>
                                                </TableRow>
                                            }

                                            {rows.map((row, idx) => (
                                                <TableRow key={idx} >
                                                    <TableCell className="report-label-cell" >
                                                        {row.SHOP_NAME}
                                                        {row.TRANSFERS_TO !== "" &&
                                                            <div style={{ fontSize: '11px', color: '#a32f2f' }} >
                                                                settles at {row.TRANSFERS_TO}
                                                            </div>
                                                        }
                                                    </TableCell>
                                                    <TableCell className="report-label-cell" >
                                                        {row.CAL_TYPE}
                                                        {/* The type in force today, when it differs from the one
                                                            that produced these figures. */}
                                                        {row.CAL_TYPE_NOW && row.CAL_TYPE_NOW !== row.CAL_TYPE &&
                                                            <div style={{ fontSize: '11px', color: '#777' }} >
                                                                now {row.CAL_TYPE_NOW}
                                                            </div>
                                                        }
                                                    </TableCell>
                                                    {group.columns.map((c) => (
                                                        <TableCell
                                                            key={c.label}
                                                            style={{ ...numCell, ...(group.tint || {}), ...(c.bold ? { fontWeight: 700 } : {}) }}
                                                        >{formatAmount(row[c.rowKey])}</TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>

                                        {rows.length > 0 &&
                                            <TableFooter>
                                                <TableRow>
                                                    {/* Server-side totals, not a re-sum of the rendered rows, so the
                                                        footer cannot drift from what the endpoint reported. */}
                                                    <TableCell style={{ ...footCell, textAlign: 'left' }} colSpan={2} >Total</TableCell>
                                                    {group.columns.map((c) => (
                                                        <TableCell key={c.label} style={footCell} >{formatAmount(totals[c.rowKey])}</TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableFooter>
                                        }
                                    </Table>
                                </TableContainer>
                            </Paper>
                        ))}

                        <div style={{ fontSize: '12px', color: '#555', padding: '8px 4px' }} >
                            Total applies each shop's calculation type for the week it belongs to: cash only,
                            card only, or both. The Type column shows the type effective across the weeks you
                            selected, which is what explains the Total; where a shop has changed type since,
                            its current one is noted underneath. A shop whose takings transfer elsewhere shows its Cash and Card
                            but a Total of zero, and the amount appears under Other Shop at the shop that
                            settles it.
                            {+ReportDetails.TRANSFERS_CONFIGURED === 0 &&
                                <span> No inter-shop transfers are configured, so every Other Shop column is zero.</span>
                            }
                        </div>
                    </Col>
                </Row>
            </section>
        );
    }
}

export default Report;
